import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Copy, Users, Check, X, Ban, Video, Mail, ShieldOff, Search, UserPlus, CircleDot, Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

/**
 * GuestManager — collapsible section for the Director Console's left rail.
 *
 * Features:
 *  - Share invite code + one-click copy (both code and full deep link)
 *  - Pending "request to join live" list with Accept / Reject / Block actions
 *  - Approved guest list with per-guest camera assignment (cam1 / cam2 / cam3 / off)
 *  - Collapsible header — the whole panel can be tucked away so it doesn't
 *    crowd the left rail when the host doesn't need it.
 *
 * Props:
 *   streamId: string
 *   inviteCode: string
 *   guests: Array<{id, user_id, user_name, status, camera_slot}>
 *   onRefresh?: () => Promise<void>  (parent's guest poll — called after actions)
 *   defaultOpen?: boolean
 */
export default function GuestManager({ streamId, inviteCode, guests = [], onRefresh, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const [busyId, setBusyId] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  const [blocked, setBlocked] = useState([]);
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [blockedBusyId, setBlockedBusyId] = useState(null);

  const { pending, live } = useMemo(() => {
    const p = [];
    const l = [];
    for (const g of guests) {
      if (g.status === "waiting") p.push(g);
      else if (g.status === "approved" || g.status === "onscreen") l.push(g);
    }
    return { pending: p, live: l };
  }, [guests]);

  const decide = async (guest, decision) => {
    setBusyId(guest.id);
    try {
      await api.post(`/live/${streamId}/guest/${guest.id}/decision`, { decision });
      const tone = decision === "approve" ? "success" : decision === "block" ? "error" : "info";
      toast[tone](
        decision === "approve" ? `${guest.user_name} accepted` :
        decision === "reject" ? `${guest.user_name} rejected` :
        decision === "block" ? `${guest.user_name} blocked from this stream` :
        decision === "remove" ? `${guest.user_name} removed` :
        `${guest.user_name} updated`,
        { id: `guest-${guest.id}` }
      );
      await onRefresh?.();
      if (decision === "block" && blockedOpen) await loadBlocked();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const assignCam = async (guest, slot) => {
    setBusyId(guest.id);
    try {
      await api.post(`/live/${streamId}/guest/${guest.id}/camera`, { slot });
      toast.success(
        slot === "off" ? `${guest.user_name} off-camera` : `${guest.user_name} → ${slot.toUpperCase()}`,
        { id: `cam-${guest.id}` }
      );
      await onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  // One-tap "approve → cam1" for a pending guest. Back-to-back API calls
  // (decision:approve then camera:cam1) so the host doesn't have to wait
  // for the refresh roundtrip between them. If either step fails we toast
  // and bail — the parent refresh will reconcile whichever did land.
  const quickPromote = async (guest, slot = "cam1") => {
    setBusyId(guest.id);
    try {
      await api.post(`/live/${streamId}/guest/${guest.id}/decision`, { decision: "approve" });
      await api.post(`/live/${streamId}/guest/${guest.id}/camera`, { slot });
      toast.success(`${guest.user_name} is live on ${slot.toUpperCase()}`, { id: `quick-${guest.id}` });
      await onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  // Per-guest "ping" — send a free-form message that lands in the guest's
  // waiting room, AND optionally start a countdown timer ("you're up in
  // 30s — get camera ready"). Used by the host or assistant director to
  // brief a guest right before bringing them on-air.
  const sendGuestMessage = async (guest, text) => {
    if (!text || !text.trim()) return false;
    try {
      await api.post(`/live/${streamId}/guest/${guest.id}/message`, { text: text.trim() });
      toast.success(`Sent to ${guest.user_name}`, { id: `gmsg-${guest.id}` });
      return true;
    } catch (e) { toast.error(formatApiError(e), { id: `gmsg-${guest.id}` }); return false; }
  };
  const setGuestCountdown = async (guest, seconds, message = "", autoPromote = false) => {
    try {
      await api.post(`/live/${streamId}/guest/${guest.id}/countdown`, {
        seconds,
        message: message || (seconds > 0 ? "Going to you in…" : ""),
        auto_promote: !!autoPromote,
      });
      toast.success(
        seconds > 0
          ? autoPromote
            ? `🎤 Pass-the-mic in ${seconds}s — ${guest.user_name} auto-on-air`
            : `${seconds}s countdown sent to ${guest.user_name}`
          : `Countdown cleared`,
        { id: `gcd-${guest.id}` },
      );
    } catch (e) { toast.error(formatApiError(e), { id: `gcd-${guest.id}` }); }
  };

  const copyInvite = () => {
    if (!inviteCode) return;
    navigator.clipboard.writeText(inviteCode);
    toast.success("Invite code copied", { id: "guest-invite-copy" });
  };

  const copyLink = () => {
    const link = `${window.location.origin}/live/${streamId}?join=${inviteCode}`;
    navigator.clipboard.writeText(link);
    toast.success("Guest join link copied", { id: "guest-link-copy" });
  };

  // Co-host link — backend issues a 24-hour pre-approved token so the
  // recipient skips the approval queue entirely and lands admitted on-air.
  const copyCohostLink = async () => {
    try {
      const { data } = await api.post(`/live/${streamId}/cohost/link`);
      if (data?.url) {
        // Server may return a relative URL when APP_PUBLIC_URL isn't set.
        const absolute = data.url.startsWith("http") ? data.url : `${window.location.origin}${data.url}`;
        navigator.clipboard.writeText(absolute);
        toast.success("Co-host link copied (24h, pre-approved)", { id: "cohost-link" });
      }
    } catch (e) {
      toast.error(formatApiError(e), { id: "cohost-link" });
    }
  };

  // Email invite — POSTs to the new backend endpoint which renders a branded
  // email with the join link and fires it off via Resend.
  const sendEmailInvite = async () => {
    const to = (inviteEmail || "").trim().toLowerCase();
    if (!to || !to.includes("@")) {
      toast.error("Enter a valid email address", { id: "invite-email" });
      return;
    }
    setSendingInvite(true);
    try {
      const { data } = await api.post(`/live/${streamId}/guest/invite-email`, {
        email: to,
        name: inviteName || null,
      });
      if (data?.ok) {
        toast.success(`Invite sent to ${to}`, { id: "invite-email" });
        setInviteEmail("");
        setInviteName("");
      } else {
        toast.error("Email send failed — the link was copied instead", { id: "invite-email" });
        if (data?.join_url) navigator.clipboard.writeText(data.join_url);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSendingInvite(false);
    }
  };

  // Blocked-user management — load list on first expand + after block/unblock
  const loadBlocked = async () => {
    try {
      const { data } = await api.get(`/live/${streamId}/guests/blocked`);
      setBlocked(data || []);
    } catch {}
  };
  useEffect(() => {
    if (blockedOpen) loadBlocked();
    // eslint-disable-next-line
  }, [blockedOpen, streamId]);

  const unblockUser = async (userId) => {
    setBlockedBusyId(userId);
    try {
      await api.delete(`/live/${streamId}/guests/blocked/${userId}`);
      toast.success("User unblocked — they can request again", { id: `unblock-${userId}` });
      await loadBlocked();
      await onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBlockedBusyId(null);
    }
  };

  return (
    <section
      id="director-guests"
      data-testid="left-guest-manager"
      className="mt-5 border-t border-white/10 pt-3 scroll-mt-20"
    >
      <button
        data-testid="guest-manager-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold text-white/60 hover:text-white transition"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Users className="w-3 h-3 text-tv-accent" /> Guests
        {pending.length > 0 && (
          <span
            data-testid="guest-manager-pending-badge"
            className="ml-auto bg-tv-live text-white text-[9px] font-black px-1.5 py-0.5 rounded-full tv-pulse"
          >
            {pending.length}
          </span>
        )}
      </button>

      {open && (
        <div className="mt-2.5 space-y-3">
          {/* Invite strip */}
          <div className="bg-black/40 border border-white/10 rounded-lg p-2">
            <div className="text-[9px] uppercase tracking-wider font-black text-white/50 mb-1.5">Invite guests</div>
            <div className="flex items-center gap-1.5">
              <code
                data-testid="guest-invite-code"
                className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-[11px] text-tv-accent font-bold truncate"
                title={inviteCode}
              >
                {inviteCode || "—"}
              </code>
              <button
                data-testid="guest-invite-copy-code"
                onClick={copyInvite}
                title="Copy code"
                className="bg-white/5 hover:bg-white/10 text-white p-1.5 rounded"
              >
                <Copy className="w-3 h-3" />
              </button>
            </div>
            <button
              data-testid="guest-invite-copy-link"
              onClick={copyLink}
              className="mt-1.5 w-full bg-tv-accent/15 hover:bg-tv-accent/25 text-tv-accent border border-tv-accent/30 text-[10px] font-bold py-1.5 rounded"
            >
              Copy full guest link
            </button>
            <button
              data-testid="guest-invite-cohost-link"
              onClick={copyCohostLink}
              title="Generate a 24h pre-approved link — recipient bypasses the approval queue."
              className="mt-1.5 w-full flex items-center justify-center gap-1 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-200 border border-fuchsia-500/40 text-[10px] font-bold py-1.5 rounded"
            >
              <Link2 className="w-3 h-3" /> Co-host this stream (pre-approved · 24h)
            </button>

            {/* Search & invite an existing Tap IN user (Pass B) */}
            <InviteExistingUserPanel streamId={streamId} onInvited={onRefresh} />

            {/* Email invite form */}
            <div className="mt-2 border-t border-white/10 pt-2 space-y-1">
              <div className="text-[9px] uppercase tracking-wider font-black text-white/50 flex items-center gap-1">
                <Mail className="w-2.5 h-2.5 text-tv-accent" /> Invite by email
              </div>
              <input
                type="text"
                data-testid="guest-invite-email-name"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="Guest name (optional)"
                className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-[10px] text-white outline-none focus:border-tv-accent"
              />
              <input
                type="email"
                data-testid="guest-invite-email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="guest@email.com"
                className="w-full bg-white/5 border border-white/10 rounded px-2 py-1 text-[10px] text-white outline-none focus:border-tv-accent"
              />
              <button
                data-testid="guest-invite-email-send"
                onClick={sendEmailInvite}
                disabled={sendingInvite}
                className="w-full flex items-center justify-center gap-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/30 text-[10px] font-bold py-1.5 rounded disabled:opacity-50"
              >
                <Mail className="w-3 h-3" /> {sendingInvite ? "Sending…" : "Send email invite"}
              </button>
              <div className="text-[9px] text-white/40 italic">SMS invites coming once you connect Twilio in Admin.</div>
            </div>
          </div>

          {/* Pending requests */}
          <div>
            <div className="text-[9px] uppercase tracking-wider font-black text-white/50 mb-1.5 flex items-center gap-1">
              Requests to join {pending.length > 0 && <span className="text-tv-live">· {pending.length}</span>}
            </div>
            {pending.length === 0 ? (
              <div data-testid="guest-pending-empty" className="text-[10px] text-white/40 italic">
                No pending requests
              </div>
            ) : (
              <ul className="space-y-1.5">
                {pending.map((g) => (
                  <li
                    key={g.id}
                    data-testid={`guest-pending-${g.id}`}
                    className="bg-white/5 border border-white/10 rounded-lg p-2"
                  >
                    <div className="text-[11px] font-bold text-white truncate">{g.user_name}</div>
                    {/* Tech-readiness badges — set by the waiting room
                        when the guest's camera + mic are open and
                        previewing locally. Lets the host pick the
                        guest who is actually ready instead of guessing. */}
                    <div className="mt-1 flex flex-wrap gap-1 text-[9px] font-black uppercase tracking-wider">
                      <span
                        data-testid={`guest-${g.id}-cam-state`}
                        className={`px-1.5 py-0.5 rounded ${g.camera_ready ? "bg-emerald-500/20 text-emerald-200 border border-emerald-500/40" : "bg-white/5 text-white/45 border border-white/10"}`}
                      >📷 {g.camera_ready ? "Cam ready" : "Cam off"}</span>
                      <span
                        data-testid={`guest-${g.id}-mic-state`}
                        className={`px-1.5 py-0.5 rounded ${g.mic_ready ? "bg-emerald-500/20 text-emerald-200 border border-emerald-500/40" : "bg-white/5 text-white/45 border border-white/10"}`}
                      >🎤 {g.mic_ready ? "Mic ready" : "Mic off"}</span>
                    </div>
                    <div className="mt-1 flex gap-1">
                      <button
                        data-testid={`guest-accept-${g.id}`}
                        disabled={busyId === g.id}
                        onClick={() => decide(g, "approve")}
                        className="flex-1 flex items-center justify-center gap-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/30 text-[10px] font-bold py-1 rounded"
                      >
                        <Check className="w-3 h-3" /> Accept
                      </button>
                      {/* One-tap "approve → CAM 1" — the fastest path
                          to bringing a waiting guest on-air. Skips the
                          two-step accept-then-assign flow so hosts can
                          land breaking-news style hand-offs. */}
                      <button
                        data-testid={`guest-onair-${g.id}`}
                        disabled={busyId === g.id}
                        onClick={() => quickPromote(g, "cam1")}
                        title="Approve and drop them into CAM 1 — appears in PIP + any multi-camera layout"
                        className="flex items-center justify-center gap-1 bg-tv-live/25 hover:bg-tv-live/40 text-red-100 border border-tv-live/40 text-[10px] font-black uppercase px-2 py-1 rounded"
                      >
                        <CircleDot className="w-3 h-3" /> On Air
                      </button>
                      <button
                        data-testid={`guest-reject-${g.id}`}
                        disabled={busyId === g.id}
                        onClick={() => decide(g, "reject")}
                        title="Reject (they can still request again)"
                        className="flex items-center justify-center gap-1 bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 text-[10px] font-bold px-2 py-1 rounded"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <button
                        data-testid={`guest-block-${g.id}`}
                        disabled={busyId === g.id}
                        onClick={() => decide(g, "block")}
                        title="Block — they cannot request again on this stream"
                        className="flex items-center justify-center gap-1 bg-red-500/15 hover:bg-red-500/25 text-red-200 border border-red-500/30 text-[10px] font-bold px-2 py-1 rounded"
                      >
                        <Ban className="w-3 h-3" />
                      </button>
                    </div>
                    <GuestPingControls
                      guest={g}
                      onSendMessage={(t) => sendGuestMessage(g, t)}
                      onCountdown={(s, autoPromote) => setGuestCountdown(g, s, "", autoPromote)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Approved / Live guests + per-guest camera assignment */}
          <div>
            <div className="text-[9px] uppercase tracking-wider font-black text-white/50 mb-1.5 flex items-center gap-1">
              <Video className="w-3 h-3 text-tv-accent" /> On stream
            </div>
            {live.length === 0 ? (
              <div data-testid="guest-live-empty" className="text-[10px] text-white/40 italic">
                No guests live
              </div>
            ) : (
              <ul className="space-y-1.5">
                {live.map((g) => {
                  const slot = g.camera_slot || "off";
                  return (
                    <li
                      key={g.id}
                      data-testid={`guest-live-${g.id}`}
                      className="bg-white/5 border border-white/10 rounded-lg p-2"
                    >
                      <div className="flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full ${g.status === "onscreen" ? "bg-tv-live tv-pulse" : "bg-emerald-400"}`} />
                        <div className="text-[11px] font-bold text-white truncate flex-1">{g.user_name}</div>
                        <button
                          data-testid={`guest-remove-${g.id}`}
                          onClick={() => decide(g, "remove")}
                          title="Remove from stream"
                          className="text-white/40 hover:text-red-400 p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="mt-1.5 flex items-center gap-1">
                        <span className="text-[9px] uppercase tracking-wider text-white/40 font-black">Cam</span>
                        {/* One-tap slot picker — four compact buttons
                            replace the old <select>. A single click is
                            always the difference between the guest
                            appearing in the program feed vs. staying
                            off-air, so easing this interaction matters
                            for fast-paced hosts. */}
                        <div
                          className="flex-1 grid grid-cols-4 gap-1"
                          data-testid={`guest-cam-picker-${g.id}`}
                          role="radiogroup"
                          aria-label={`Camera slot for ${g.user_name}`}
                        >
                          {[
                            { key: "off",  label: "Off" },
                            { key: "cam1", label: "1" },
                            { key: "cam2", label: "2" },
                            { key: "cam3", label: "3" },
                          ].map(({ key, label }) => {
                            const active = slot === key;
                            return (
                              <button
                                key={key}
                                type="button"
                                disabled={busyId === g.id}
                                role="radio"
                                aria-checked={active}
                                data-testid={`guest-cam-${key}-${g.id}`}
                                onClick={() => { if (!active) assignCam(g, key); }}
                                title={key === "off" ? "Take off camera" : `Send to ${key.toUpperCase()} — appears in PIP + multi-camera layouts`}
                                className={`text-[10px] font-black uppercase tracking-wider rounded py-1 transition ${
                                  active
                                    ? key === "off"
                                      ? "bg-white/25 text-white border border-white/30"
                                      : "bg-tv-accent text-black border border-tv-accent shadow"
                                    : "bg-black/40 text-white/70 border border-white/10 hover:bg-white/10"
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Blocked users — collapsible sub-panel */}
          <div>
            <button
              data-testid="guest-blocked-toggle"
              onClick={() => setBlockedOpen((v) => !v)}
              className="w-full flex items-center gap-1.5 text-[9px] uppercase tracking-wider font-black text-white/50 hover:text-white"
            >
              {blockedOpen ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}
              <Ban className="w-2.5 h-2.5 text-red-300" /> Blocked
              {blocked.length > 0 && (
                <span className="ml-auto bg-red-500/20 text-red-200 border border-red-500/30 text-[9px] font-black px-1.5 py-0.5 rounded-full">
                  {blocked.length}
                </span>
              )}
            </button>
            {blockedOpen && (
              <div className="mt-1.5 space-y-1">
                {blocked.length === 0 ? (
                  <div data-testid="guest-blocked-empty" className="text-[10px] text-white/40 italic">
                    No blocked users
                  </div>
                ) : (
                  blocked.map((b) => (
                    <div
                      key={b.user_id}
                      data-testid={`guest-blocked-${b.user_id}`}
                      className="flex items-center gap-1.5 bg-red-500/5 border border-red-500/20 rounded-lg p-1.5"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-bold text-white truncate">{b.name}</div>
                        {b.email && <div className="text-[9px] text-white/40 truncate">{b.email}</div>}
                      </div>
                      <button
                        data-testid={`guest-unblock-${b.user_id}`}
                        disabled={blockedBusyId === b.user_id}
                        onClick={() => unblockUser(b.user_id)}
                        title="Unblock — they can request again"
                        className="flex items-center gap-1 bg-white/5 hover:bg-emerald-500/20 hover:text-emerald-200 text-white/70 border border-white/10 text-[10px] font-bold px-2 py-1 rounded"
                      >
                        <ShieldOff className="w-3 h-3" /> Unblock
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * InviteExistingUserPanel — search existing Tap IN users by name/handle/email
 * and one-click invite them onto the guest list (Pass B). Online users
 * (heartbeat < 2 min) surface at the top with a green dot.
 */
function InviteExistingUserPanel({ streamId, onInvited }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [onlyOnline, setOnlyOnline] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invitingId, setInvitingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/live/${streamId}/users/searchable`, {
          params: { q, only_online: onlyOnline ? true : undefined },
        });
        setResults(data || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(id);
  }, [q, onlyOnline, open, streamId]);

  const invite = async (u) => {
    setInvitingId(u.user_id);
    try {
      await api.post(`/live/${streamId}/guest/invite-user`, { user_id: u.user_id, email_notify: true });
      toast.success(`Invited ${u.name || u.email}`, { id: `inv-${u.user_id}` });
      setResults((prev) => prev.filter((r) => r.user_id !== u.user_id));
      await onInvited?.();
    } catch (e) {
      toast.error(formatApiError(e), { id: `inv-${u.user_id}` });
    } finally {
      setInvitingId(null);
    }
  };

  return (
    <div className="mt-2 border-t border-white/10 pt-2 space-y-1.5" data-testid="guest-invite-user-panel">
      <button
        data-testid="guest-invite-user-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1.5 text-[9px] uppercase tracking-wider font-black text-white/50 hover:text-white"
      >
        {open ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}
        <UserPlus className="w-2.5 h-2.5 text-tv-accent" /> Invite Tap IN user
      </button>
      {open && (
        <>
          <div className="relative">
            <input
              data-testid="guest-invite-user-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, handle, or email…"
              className="w-full bg-white/5 border border-white/10 rounded pl-7 pr-2 py-1 text-[10px] text-white outline-none focus:border-tv-accent"
            />
            <Search className="w-3 h-3 text-white/40 absolute left-2 top-1/2 -translate-y-1/2" />
          </div>
          <label className="flex items-center gap-1.5 text-[9px] text-white/60 cursor-pointer">
            <input
              data-testid="guest-invite-user-online-filter"
              type="checkbox"
              checked={onlyOnline}
              onChange={(e) => setOnlyOnline(e.target.checked)}
              className="accent-tv-accent"
            />
            Only users currently online
          </label>
          <div className="max-h-[200px] overflow-y-auto space-y-0.5 pr-0.5" data-testid="guest-invite-user-results">
            {loading && <div className="text-white/40 text-[10px] py-2 text-center">Searching…</div>}
            {!loading && results.length === 0 && (
              <div className="text-white/40 text-[10px] py-2 text-center">
                {q.trim() || onlyOnline ? "No matching users." : "Type to search…"}
              </div>
            )}
            {results.map((u) => (
              <div
                key={u.user_id}
                data-testid={`guest-invite-user-result-${u.user_id}`}
                className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 rounded px-1.5 py-1"
              >
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-5 h-5 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[8px] font-black shrink-0">
                    {(u.name || u.email || "?")[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0 leading-tight">
                  <div className="text-[10px] text-white font-bold truncate flex items-center gap-1">
                    {u.name || u.handle || u.email}
                    {u.is_online && <CircleDot className="w-2 h-2 text-emerald-400" />}
                  </div>
                  <div className="text-[9px] text-white/40 truncate">{u.handle ? `@${u.handle}` : u.email}</div>
                </div>
                <button
                  data-testid={`guest-invite-user-send-${u.user_id}`}
                  disabled={invitingId === u.user_id}
                  onClick={() => invite(u)}
                  className="shrink-0 bg-tv-accent hover:bg-yellow-400 text-black text-[9px] font-black px-2 py-1 rounded disabled:opacity-50"
                >
                  {invitingId === u.user_id ? "…" : "Invite"}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}



/**
 * GuestPingControls — compact "ping the guest" UI shown under each pending
 * guest tile. The director can fire a quick text message ("Stand by") or
 * a countdown timer (15s / 30s / 60s / custom) that lights up in the
 * guest's waiting room. Used by the host AND by Assistant Directors.
 */
function GuestPingControls({ guest, onSendMessage, onCountdown }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  // "Pass the mic" — when on, the countdown auto-promotes the guest to
  // on-air the moment the timer hits 0. Off by default so a stray click
  // never accidentally drops a viewer onto the broadcast.
  const [passMic, setPassMic] = useState(false);

  const fire = async (preset) => {
    setBusy(true);
    if (preset.text) await onSendMessage(preset.text);
    if (typeof preset.seconds === "number") {
      // Don't auto-promote on the "Clear" preset (seconds=0) even if the
      // pass-mic toggle is on — a clear is a clear.
      const auto = preset.seconds > 0 ? passMic : false;
      await onCountdown(preset.seconds, auto);
    }
    setBusy(false);
  };

  const sendCustom = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    const ok = await onSendMessage(text);
    setBusy(false);
    if (ok) setText("");
  };

  return (
    <div className="mt-1.5" data-testid={`guest-ping-${guest.id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`guest-ping-toggle-${guest.id}`}
        className="text-[9px] uppercase tracking-wider font-black text-white/55 hover:text-tv-accent inline-flex items-center gap-1 px-1"
      >
        💬 {open ? "Hide ping controls" : "Message / countdown"}
      </button>
      {open && (
        <div className="mt-1 rounded border border-white/10 bg-black/40 p-1.5 space-y-1">
          {/* "Pass the mic" toggle — when on, the timer's 0 mark auto-
              promotes this guest to on-air without the host clicking. */}
          <label className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider font-black text-white/65 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={passMic}
              onChange={(e) => setPassMic(e.target.checked)}
              data-testid={`guest-ping-pass-mic-${guest.id}`}
              className="accent-tv-accent"
            />
            🎤 Pass the mic at 0 (auto-bring on-air)
          </label>
          <div className="flex flex-wrap gap-1">
            {[
              { lbl: "Stand by",   text: "Stand by — going to you soon." },
              { lbl: "15s",        seconds: 15, text: passMic ? "You're on in 15 — get ready." : "Going to you in 15 seconds — get camera ready." },
              { lbl: "30s",        seconds: 30, text: passMic ? "You're on in 30 — get ready." : "Going to you in 30 seconds — get camera ready." },
              { lbl: "1m",         seconds: 60, text: passMic ? "You're on in 1 minute — get ready." : "Going to you in 1 minute — get camera + mic ready." },
              { lbl: "Clear",      seconds: 0 },
            ].map((p) => (
              <button
                key={p.lbl}
                type="button"
                disabled={busy}
                onClick={() => fire(p)}
                data-testid={`guest-ping-preset-${guest.id}-${p.lbl.replace(/\s+/g, "-").toLowerCase()}`}
                className="text-[9px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded bg-white/5 hover:bg-white/15 text-white/70 border border-white/10"
              >{p.lbl}</button>
            ))}
          </div>
          <form onSubmit={sendCustom} className="flex gap-1">
            <input
              data-testid={`guest-ping-custom-${guest.id}`}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Custom note (e.g. mute that fan)"
              maxLength={280}
              className="flex-1 bg-white/5 border border-white/10 rounded px-1.5 py-1 text-[10px] text-white outline-none"
            />
            <button
              type="submit"
              disabled={busy || !text.trim()}
              data-testid={`guest-ping-send-${guest.id}`}
              className="bg-tv-accent disabled:opacity-50 text-black font-black text-[9px] uppercase tracking-wider px-1.5 py-1 rounded"
            >Send</button>
          </form>
        </div>
      )}
    </div>
  );
}
